/** How long a card is followed while its approval view loads and sizes itself. */
export const FOLLOW_MS = 4000;

/** The scroll areas a choice card sits in (the approvals strip, the thread): marked, so the card
 * that is followed is scrolled within its own one. Spread on the scroll area. */
export const CARD_SCROLLER_PROPS = { "data-card-scroller": "" } as const;
const CARD_SCROLLER = "data-card-scroller";

export function cardScroller(card: HTMLElement): HTMLElement | null {
  return card.parentElement?.closest<HTMLElement>(`[${CARD_SCROLLER}]`) ?? null;
}

/**
 * Keep ``card``'s bottom edge (where an approval's buttons are) in view in ``scroller`` while the
 * card grows (its frame sizes itself after its view loads), for ``ms`` or until the user scrolls:
 * any scroll this did not make itself, by keyboard, scrollbar, wheel or touch (the wheel and touch
 * over the card's frame never reach this page, hence the position check). Returns ``stop``.
 */
export function followBottom(card: HTMLElement, scroller: HTMLElement, ms = FOLLOW_MS) {
  let last = scroller.scrollTop;
  let stopped = false;
  let timer = 0;

  const toBottom = () => {
    const below = card.getBoundingClientRect().bottom - scroller.getBoundingClientRect().bottom;
    if (below > 0) scroller.scrollTop += below;
    last = scroller.scrollTop;
  };
  const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(toBottom);
  const onScroll = () => {
    if (Math.abs(scroller.scrollTop - last) > 1) stop();
  };
  const stop = () => {
    if (stopped) return;
    stopped = true;
    observer?.disconnect();
    window.clearTimeout(timer);
    scroller.removeEventListener("scroll", onScroll);
    scroller.removeEventListener("wheel", stop);
    scroller.removeEventListener("touchstart", stop);
  };

  toBottom();
  observer?.observe(card);
  scroller.addEventListener("scroll", onScroll, { passive: true });
  scroller.addEventListener("wheel", stop, { once: true, passive: true });
  scroller.addEventListener("touchstart", stop, { once: true, passive: true });
  timer = window.setTimeout(stop, ms);
  return stop;
}
